/* ==========================================================================
   Product Section Component (PORTAL-26)

   A self-contained product subform: each instance owns its own
   `react-hook-form` instance via FormProvider, so multiple sections on a
   page are fully isolated from one another. The parent collects each
   section's data through an imperative handle on submit.
   ========================================================================== */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { ProductSelector } from '@/components/forms/ProductSelector'
import { PrescriptionDetails } from '@/components/forms/PrescriptionDetails'
import { Pill, X } from 'lucide-react'
import {
  blankCustomFieldValues,
  getMissingRequiredCustomFields,
  isBlankCustomFieldValue,
} from '@/utils/customFields'
import type { CatalogProductForPortal, DraftLineItem } from '@/api/types'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface ProductSectionFormData {
  product_id: string
  dose_specific_directions: string
  quantity: string
  unit_amount: string
  days_supply: number
  refills: number
  custom_field_values: Record<string, string | number | boolean>
}

/**
 * Per-product line item the parent merges into the bulk submission payload.
 * Field names match the backend `PrescriptionLineItem` schema (catalog_product_id).
 * Null-when-unmodified semantics: only send fields the prescriber actually
 * changed from catalog defaults.
 */
export interface ProductSectionPayload {
  catalog_product_id: string
  dose_specific_directions: string | null
  quantity: string | null
  unit_amount: string | null
  days_supply: number | null
  refills: number | null
  custom_field_values: Record<string, string | number | boolean> | null
}

export interface ProductSectionHandle {
  validate: () => Promise<boolean>
  getPayload: () => ProductSectionPayload | null
  /**
   * Full current values for a "save for later" draft (PORTAL-61). Unlike
   * {@link getPayload}, which sends null for any field equal to the catalog
   * default, this returns the actual on-screen values for every field so the
   * draft restores byte-for-byte regardless of later catalog changes.
   */
  getDraftPayload: () => DraftLineItem | null
  getProduct: () => CatalogProductForPortal | null
}

/** Per-product values used to restore a saved draft into this section. */
export interface ProductSectionPrefillValues {
  dose_specific_directions?: string | null
  quantity?: string | null
  unit_amount?: string | null
  days_supply?: number | null
  refills?: number | null
}

export interface ProductSectionProps {
  index: number
  disabled?: boolean
  /** When provided, renders a remove button. The first section omits this. */
  onRemove?: () => void
  /**
   * Notifies the parent whether this section is complete enough to submit: a
   * product is selected AND every required custom field on it has a value
   * (PORTAL-147). Feeds the parent's submit gating.
   */
  onReadyChange?: (isReady: boolean) => void
  /**
   * Seeds the section's product on mount (prescribe-again flow). The inner
   * ProductSelector auto-selects the matching product once the catalog loads;
   * details fall back to catalog defaults, matching the pre-bulk behavior.
   */
  prefillProductId?: string
  /**
   * Seeds the section's custom field values on mount (prescribe-again flow).
   * Paired with {@link prefillProductId}: preserved through the prefill
   * auto-select instead of being reset, so re-prescribing keeps prior entries.
   */
  prefillCustomFieldValues?: Record<string, string | number | boolean>
  /**
   * Seeds the section's per-product field values for a saved-draft restore
   * (PORTAL-61). Re-applied after the catalog auto-select so saved edits to
   * dose/quantity/days-supply/refills survive (prescribe-again leaves these
   * undefined and falls back to catalog defaults).
   */
  prefillValues?: ProductSectionPrefillValues
}

/* --------------------------------------------------------------------------
   Defaults
   -------------------------------------------------------------------------- */

// Blank/unset sentinel for the numeric form fields, matching the one
// PrescriptionDetails uses (see its days_supply/refills handling). Distinct from
// 0, which is a real value — a cleared field must restore as blank, not zero.
const BLANK_NUMBER = '' as unknown as number

const defaultValues: ProductSectionFormData = {
  product_id: '',
  dose_specific_directions: '',
  quantity: '',
  unit_amount: '',
  days_supply: 0,
  refills: 0,
  custom_field_values: {},
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export const ProductSection = forwardRef<ProductSectionHandle, ProductSectionProps>(
  function ProductSection(
    {
      index,
      disabled = false,
      onRemove,
      onReadyChange,
      prefillProductId,
      prefillCustomFieldValues,
      prefillValues,
    },
    ref,
  ) {
    const methods = useForm<ProductSectionFormData>({
      defaultValues: {
        ...defaultValues,
        product_id: prefillProductId ?? '',
        custom_field_values: prefillCustomFieldValues ?? {},
        // Draft restore: fall back to the catalog default only when a field is
        // absent (undefined). An explicit null means the prescriber cleared it,
        // so restore the form's blank value ('' for text, BLANK_NUMBER for the
        // numeric fields — not 0, which is a real value).
        ...(prefillValues
          ? {
              dose_specific_directions:
                prefillValues.dose_specific_directions === undefined
                  ? defaultValues.dose_specific_directions
                  : (prefillValues.dose_specific_directions ?? ''),
              quantity:
                prefillValues.quantity === undefined
                  ? defaultValues.quantity
                  : (prefillValues.quantity ?? ''),
              unit_amount:
                prefillValues.unit_amount === undefined
                  ? defaultValues.unit_amount
                  : (prefillValues.unit_amount ?? ''),
              days_supply:
                prefillValues.days_supply === undefined
                  ? defaultValues.days_supply
                  : (prefillValues.days_supply ?? BLANK_NUMBER),
              refills:
                prefillValues.refills === undefined
                  ? defaultValues.refills
                  : (prefillValues.refills ?? BLANK_NUMBER),
            }
          : {}),
      },
      mode: 'onSubmit',
    })

    const [selectedProduct, setSelectedProduct] =
      useState<CatalogProductForPortal | null>(null)

    // The prescribe-again auto-select fires once when the catalog loads; that
    // first selection must preserve the seeded custom field values rather than
    // reset them. Any later (user-driven) product change resets as usual.
    const skipNextResetRef = useRef(!!prefillProductId)

    const handleProductSelect = useCallback(
      (product: CatalogProductForPortal | null) => {
        setSelectedProduct(product)
        const preserveSeed =
          skipNextResetRef.current && product?.id === prefillProductId
        skipNextResetRef.current = false
        // Reset custom field values when product changes — same behavior as
        // the original single-product form. PORTAL-147: reset to an explicit
        // blank per field of the NEW product rather than `{}`. RHF's setValue
        // walks the keys of the object it is given, so `{}` clears the stored
        // value but never touches the DOM; the inputs are uncontrolled and React
        // reuses the node when the new product reuses a field key, leaving the
        // previous product's text on screen over an empty form value. Errors go
        // too, so a stale "X is required" can't ride along on a shared key.
        if (!preserveSeed) {
          methods.setValue('custom_field_values', blankCustomFieldValues(product))
          methods.clearErrors('custom_field_values')
        }
        // Readiness is derived during render below — reporting it here as well
        // would flash ready=true for a product whose required custom fields are
        // still empty.
      },
      [methods, prefillProductId],
    )

    // PORTAL-147: a section is submit-ready only when a product is selected AND
    // every required custom field on it has a value. Derived during render:
    // watch('custom_field_values') hands back the same object identity on every
    // render (RHF mutates it in place), so it must never be an effect dep — the
    // derived boolean is what actually changes.
    const customFieldValues = methods.watch('custom_field_values')
    const missingRequiredCustomFields = getMissingRequiredCustomFields(
      selectedProduct?.custom_fields,
      customFieldValues,
    )
    const isReady = !!selectedProduct && missingRequiredCustomFields.length === 0

    // Report readiness on mount and whenever it changes. Parent uses this to
    // gate the submit button. `onReadyChange` is a fresh closure on every parent
    // render, so this re-runs often; the parent's setter bails out when the
    // value is unchanged.
    useEffect(() => {
      onReadyChange?.(isReady)
    }, [isReady, onReadyChange])

    // Draft restore (PORTAL-61): re-apply the saved per-product values once the
    // prefill product auto-selects. PrescriptionDetails (a child) resets these
    // fields to the catalog defaults whenever the product loads; because parent
    // effects run AFTER child effects, this restores the saved values so the
    // draft comes back exactly as the user left it.
    const draftSeedRef = useRef(prefillValues ?? null)
    useEffect(() => {
      const seed = draftSeedRef.current
      if (!seed) return
      if (!selectedProduct || selectedProduct.id !== prefillProductId) return
      draftSeedRef.current = null
      // Apply any field the draft actually carried (!== undefined). A null means
      // the prescriber cleared it, so restore the blank value rather than leaving
      // the catalog default the child just applied.
      if (seed.dose_specific_directions !== undefined)
        methods.setValue('dose_specific_directions', seed.dose_specific_directions ?? '')
      if (seed.quantity !== undefined) methods.setValue('quantity', seed.quantity ?? '')
      if (seed.unit_amount !== undefined)
        methods.setValue('unit_amount', seed.unit_amount ?? '')
      if (seed.days_supply !== undefined)
        methods.setValue('days_supply', seed.days_supply ?? BLANK_NUMBER)
      if (seed.refills !== undefined)
        methods.setValue('refills', seed.refills ?? BLANK_NUMBER)
    }, [selectedProduct, prefillProductId, methods])

    useImperativeHandle(
      ref,
      () => ({
        validate: () => methods.trigger(),
        getProduct: () => selectedProduct,
        getPayload: () => {
          if (!selectedProduct) return null
          const data = methods.getValues()

          const doseModified =
            data.dose_specific_directions !== selectedProduct.dose_specific_directions
          const quantityModified =
            data.quantity !== selectedProduct.quantity ||
            data.unit_amount !== selectedProduct.unit_amount
          const daysSupplyModified = data.days_supply !== selectedProduct.days_supply
          const refillsModified = data.refills !== selectedProduct.refills

          // Filter custom field values: drop empty/undefined/null entries —
          // and NaN, which an emptied number input produces and JSON.stringify
          // would send as null (PORTAL-147).
          let customFieldValues: Record<string, string | number | boolean> | null = null
          if (
            selectedProduct.custom_fields &&
            selectedProduct.custom_fields.length > 0 &&
            data.custom_field_values
          ) {
            const filtered: Record<string, string | number | boolean> = {}
            for (const [key, value] of Object.entries(data.custom_field_values)) {
              if (!isBlankCustomFieldValue(value)) {
                filtered[key] = value
              }
            }
            if (Object.keys(filtered).length > 0) {
              customFieldValues = filtered
            }
          }

          return {
            catalog_product_id: data.product_id,
            dose_specific_directions: doseModified ? data.dose_specific_directions : null,
            quantity: quantityModified ? data.quantity : null,
            unit_amount: quantityModified ? data.unit_amount : null,
            days_supply: daysSupplyModified ? data.days_supply : null,
            refills: refillsModified ? data.refills : null,
            custom_field_values: customFieldValues,
          }
        },
        getDraftPayload: () => {
          // A draft stores the FULL on-screen values (not the override-or-null
          // submit shape) so it restores exactly as saved.
          if (!selectedProduct) return null
          const data = methods.getValues()
          const num = (v: unknown) =>
            typeof v === 'number' && !Number.isNaN(v) ? v : null

          let customFieldValues: Record<string, string | number | boolean> | null = null
          if (
            selectedProduct.custom_fields &&
            selectedProduct.custom_fields.length > 0 &&
            data.custom_field_values
          ) {
            const filtered: Record<string, string | number | boolean> = {}
            for (const [key, value] of Object.entries(data.custom_field_values)) {
              if (!isBlankCustomFieldValue(value)) {
                filtered[key] = value
              }
            }
            if (Object.keys(filtered).length > 0) {
              customFieldValues = filtered
            }
          }

          const days = num(data.days_supply)
          // quantity is a string field with a gt=0 constraint server-side, so
          // "0"/negatives/non-numeric are truthy but invalid — coerce them to
          // null (unset) while preserving formatting like "2.5".
          const qty = Number(data.quantity)
          return {
            catalog_product_id: data.product_id,
            dose_specific_directions: data.dose_specific_directions?.trim() || null,
            quantity:
              data.quantity !== '' && Number.isFinite(qty) && qty > 0
                ? String(data.quantity)
                : null,
            unit_amount: data.unit_amount || null,
            // days_supply has a gt=0 constraint server-side; 0 means "unset".
            days_supply: days != null && days > 0 ? days : null,
            refills: num(data.refills),
            custom_field_values: customFieldValues,
            product_display_name: selectedProduct.display_name,
          }
        },
      }),
      [methods, selectedProduct],
    )

    return (
      <FormProvider {...methods}>
        <Card>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    selectedProduct
                      ? 'bg-[var(--color-success-light)] text-[var(--color-success)]'
                      : 'bg-[var(--color-soft-lavender)] text-[var(--pharmacy-primary)]'
                  }`}
                >
                  <Pill className="h-4 w-4" />
                </div>
                <h2 className="text-lg font-semibold text-[var(--color-charcoal)]">
                  Product {index + 1}
                </h2>
                {selectedProduct && (
                  <span className="text-xs font-medium text-[var(--color-success)] bg-[var(--color-success-light)] px-2 py-0.5 rounded-full">
                    Selected
                  </span>
                )}
              </div>
              {onRemove && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onRemove}
                  disabled={disabled}
                  aria-label={`Remove product ${index + 1}`}
                  className="text-[var(--color-gray-500)] hover:text-[var(--color-error)]"
                >
                  <X className="h-4 w-4" />
                  Remove
                </Button>
              )}
            </div>

            <ProductSelector onProductSelect={handleProductSelect} disabled={disabled} />
          </div>
        </Card>

        {selectedProduct && (
          <PrescriptionDetails product={selectedProduct} disabled={disabled} />
        )}

        {/* PORTAL-147: say WHY submit is greyed out. A disabled button with no
            explanation is its own dead end — the prescriber has to guess which
            of the Additional Information fields is still missing. */}
        {selectedProduct && missingRequiredCustomFields.length > 0 && (
          <p className="text-sm text-[var(--color-warning)]">
            Complete the required Additional Information for Product {index + 1}{' '}
            before submitting:{' '}
            {missingRequiredCustomFields.map((f) => f.label).join(', ')}.
          </p>
        )}
      </FormProvider>
    )
  },
)

export default ProductSection
